import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LAUNCH } from '../lib/launch';
import { BetaBadge } from './ComingSoon';
import {
  AOS_ELIGIBLE,
  ATTY_REVIEW_PRICES,
  createPathwayCheckoutSession,
  getPathway,
  peekPendingPurchase,
  requestPathwayPurchase,
  resolvePurchaseTarget,
  setPendingPurchase,
} from '../lib/pathways';
import { API_BASE } from '../lib/apiBase';

/**
 * Optional add-ons before Stripe Checkout.
 */
export const CheckoutAddonModal = ({ pathway, onClose, onConfirm }) => {
  const [addAttyReview, setAddAttyReview] = useState(false);
  const [addAos, setAddAos] = useState(false);

  const showAos = AOS_ELIGIBLE.has(pathway?.id) && pathway?.id !== 'aos-i485';
  const attyPrice = ATTY_REVIEW_PRICES[pathway?.id] || 99;

  return (
    <div className="fixed inset-0 bg-[#303035]/75 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-black font-cormorant">Enhance Your Package</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>

        <p className="text-gray-600 mb-6">
          You&apos;re purchasing <strong>{pathway?.name}</strong> ({pathway?.price}). Add optional upgrades:
        </p>

        <div className="space-y-4 mb-8">
          <label className={`flex items-start gap-3 p-4 border-2 rounded-lg transition-all ${LAUNCH.attorneyReviewPurchases ? 'cursor-pointer' : 'opacity-70'} ${addAttyReview ? 'border-yellow-500 bg-yellow-50' : 'border-gray-200 hover:border-gray-300'}`}>
            <input
              type="checkbox"
              checked={addAttyReview}
              disabled={!LAUNCH.attorneyReviewPurchases}
              onChange={(e) => setAddAttyReview(e.target.checked)}
              className="mt-1 accent-yellow-500"
            />
            <div>
              <div className="font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
                Attorney Draft Review <span className="text-yellow-600">+${attyPrice}</span>
                {!LAUNCH.attorneyReviewPurchases && <BetaBadge />}
              </div>
              <div className="text-sm text-gray-600">
                {LAUNCH.attorneyReviewPurchases
                  ? 'Attorney reviews your completed workflow draft and provides inline comments before you file.'
                  : 'Visible in beta. Attorney review is not available for purchase on this launch.'}
              </div>
            </div>
          </label>

          {showAos && (
            <label className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${addAos ? 'border-yellow-500 bg-yellow-50' : 'border-gray-200 hover:border-gray-300'}`}>
              <input
                type="checkbox"
                checked={addAos}
                onChange={(e) => setAddAos(e.target.checked)}
                className="mt-1 accent-yellow-500"
              />
              <div>
                <div className="font-semibold text-gray-900">
                  Adjustment of Status (I-485) <span className="text-yellow-600">+$100</span>
                </div>
                <div className="text-sm text-gray-600">
                  Add the AOS pathway at a discounted rate since you hold a qualifying petition entitlement.
                </div>
              </div>
            </label>
          )}
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => onConfirm({ attorney_review: LAUNCH.attorneyReviewPurchases && addAttyReview, aos_addon: addAos })}
            className="flex-1 bg-yellow-500 text-black py-3 rounded-lg font-semibold hover:bg-yellow-400 transition-colors"
          >
            Proceed to Payment
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-3 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Sticky conversion bar after eligibility results (or any recommended pathway).
 */
export const PurchaseStickyBar = ({ pathwayId, onPurchase, secondaryHref = '/pricing' }) => {
  const pathway = getPathway(pathwayId);
  if (!pathway) return null;

  return (
    <div className="purchase-sticky-bar">
      <div className="purchase-sticky-inner">
        <div className="min-w-0">
          <p className="text-[10px] tracking-[0.16em] uppercase text-[#D4A017] font-semibold mb-0.5">
            Recommended next step
          </p>
          <p className="text-white text-sm sm:text-base font-semibold truncate">
            Continue to {pathway.name} — {pathway.price}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <a
            href={secondaryHref}
            className="hidden sm:inline-block text-white/70 text-xs hover:text-white border border-white/25 px-3 py-2"
          >
            All pathways
          </a>
          <button
            type="button"
            onClick={() => onPurchase(pathwayId)}
            className="bg-[#D4A017] text-[#222226] px-4 sm:px-5 py-2.5 text-xs font-bold tracking-widest uppercase hover:bg-[#E8B41F] transition-colors"
          >
            Purchase {pathway.price}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Opens addon modal or auth redirect; on confirm sends user to Stripe.
 */
export function usePathwayPurchase({ onNeedAuth } = {}) {
  const [checkoutPathway, setCheckoutPathway] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState('');

  const beginPurchase = (pathwayId) => {
    const pathway = getPathway(pathwayId);
    if (!pathway) {
      setError('Unknown pathway');
      return;
    }
    setPendingPurchase(pathwayId);
    const token = localStorage.getItem('token');
    if (!token) {
      if (onNeedAuth) onNeedAuth(pathwayId);
      return;
    }
    setCheckoutPathway(pathway);
  };

  const confirmCheckout = async ({ attorney_review, aos_addon }) => {
    if (!checkoutPathway) return;
    setPurchasing(true);
    setError('');
    try {
      const url = await createPathwayCheckoutSession(checkoutPathway.id, {
        attorney_review,
        aos_addon,
      });
      window.location.href = url;
    } catch (err) {
      setError(err.message || 'Checkout failed');
      setPurchasing(false);
    }
  };

  const modal = checkoutPathway ? (
    <CheckoutAddonModal
      pathway={checkoutPathway}
      onClose={() => !purchasing && setCheckoutPathway(null)}
      onConfirm={confirmCheckout}
    />
  ) : null;

  return {
    beginPurchase,
    checkoutPathway,
    setCheckoutPathway,
    purchasing,
    error,
    setError,
    modal,
  };
}

function purchaseButtonLabel(pathway, owned) {
  if (pathway && !owned) return `Purchase ${pathway.name} — ${pathway.price}`;
  return 'Purchase a Pathway';
}

/**
 * Gold purchase control for page headers. Dispatches to GlobalPurchaseChrome.
 */
export const PurchaseNavButton = ({ compact = true }) => {
  const location = useLocation();
  const [label, setLabel] = useState('Purchase a Pathway');

  useEffect(() => {
    const update = () => {
      const id = resolvePurchaseTarget(location.pathname);
      const pathway = getPathway(id);
      setLabel(pathway ? `Purchase ${pathway.price}` : 'Purchase a Pathway');
    };
    update();
    window.addEventListener('immigrate:pending-purchase', update);
    return () => window.removeEventListener('immigrate:pending-purchase', update);
  }, [location.pathname]);

  return (
    <button
      type="button"
      data-testid="nav-purchase-cta"
      onClick={requestPathwayPurchase}
      className={compact ? 'purchase-nav-btn' : 'purchase-cta-btn px-5 py-2.5 text-xs'}
    >
      {label}
    </button>
  );
};

/**
 * Persistent highlighted purchase CTA on every authenticated and public page.
 * Uses the current workflow pathway, else the eligibility recommendation.
 */
export const GlobalPurchaseChrome = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [owned, setOwned] = useState([]);
  const [pendingId, setPendingId] = useState(() => peekPendingPurchase());

  const fromWorkflow = location.pathname.match(/^\/workflow\/([^/?#]+)/)?.[1];
  const targetId = (getPathway(fromWorkflow) && fromWorkflow) || (getPathway(pendingId) && pendingId) || null;
  const pathway = getPathway(targetId);
  const alreadyOwned = Boolean(pathway && owned.includes(pathway.id));

  const {
    beginPurchase,
    modal,
    error,
  } = usePathwayPurchase({
    onNeedAuth: () => {
      window.location.href = '/?auth=1&purchase=1';
    },
  });

  useEffect(() => {
    const match = location.pathname.match(/^\/workflow\/([^/?#]+)/);
    if (match && getPathway(match[1])) setPendingPurchase(match[1]);
  }, [location.pathname]);

  useEffect(() => {
    const onPending = (e) => setPendingId(e.detail || peekPendingPurchase());
    window.addEventListener('immigrate:pending-purchase', onPending);
    return () => window.removeEventListener('immigrate:pending-purchase', onPending);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setOwned([]);
      return;
    }
    let cancelled = false;
    fetch(`${API_BASE}/api/me/entitlement`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled) setOwned(data?.active_pathways || []);
      })
      .catch(() => {
        if (!cancelled) setOwned([]);
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  useEffect(() => {
    document.body.classList.add('has-purchase-bar');
    return () => document.body.classList.remove('has-purchase-bar');
  }, []);

  const startPurchase = () => {
    if (pathway && !alreadyOwned) {
      beginPurchase(pathway.id);
      return;
    }
    if (location.pathname !== '/pricing') {
      navigate('/pricing');
    }
  };

  const startPurchaseRef = useRef(startPurchase);
  startPurchaseRef.current = startPurchase;

  useEffect(() => {
    const handler = () => startPurchaseRef.current();
    window.addEventListener('immigrate:purchase', handler);
    return () => window.removeEventListener('immigrate:purchase', handler);
  }, []);

  if (location.pathname.startsWith('/admin')) return modal;

  const headline = pathway && !alreadyOwned
    ? `Unlock ${pathway.name}`
    : 'Buy a pathway to unlock your workflow';
  const sub = pathway && !alreadyOwned
    ? `One-time ${pathway.price}. Payment is on this bar — you do not need to hunt for a link.`
    : 'Choose a pathway and pay once. Eligibility is optional.';

  return (
    <>
      {modal}
      <div className="purchase-sticky-bar" data-testid="global-purchase-bar" role="region" aria-label="Purchase pathway">
        <div className="purchase-sticky-inner">
          <div className="min-w-0">
            <p className="text-[10px] tracking-[0.16em] uppercase text-[#D4A017] font-bold mb-0.5">
              Pay here
            </p>
            <p className="text-white text-sm sm:text-base font-bold truncate">{headline}</p>
            <p className="text-white/65 text-xs hidden sm:block truncate">{sub}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <a
              href="/pricing"
              className="hidden sm:inline-block text-white/80 text-xs hover:text-white border border-white/30 px-3 py-2 font-semibold"
            >
              All pathways
            </a>
            <button
              type="button"
              data-testid="global-purchase-cta"
              onClick={startPurchase}
              className="purchase-cta-btn px-4 sm:px-6 py-3 text-xs sm:text-sm"
            >
              {purchaseButtonLabel(pathway && !alreadyOwned ? pathway : null, false)}
            </button>
          </div>
        </div>
        {error && (
          <p className="text-center text-red-300 text-xs mt-2">{error}</p>
        )}
      </div>
    </>
  );
};
